import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { Apps, BarChart as BarChartIcon, Crop, PhotoLibrary, FolderSpecial, Palette } from '@mui/icons-material';
import AdminPhotos from './Photos';
import CustomFrames from './CustomFrames';
import ThemeList from '../Theme/ThemeList';
import UserCollections from '../User/Collections';
import AdminIcons from './Icons';
import MPhotoSectionTabs from '../shared/MPhotoSectionTabs';
import GalleryStatsPanel from '../shared/GalleryStatsPanel';
import { useI18n } from '../../i18n/useI18n';

interface AdminMPhotoProProps {
  userId: string;
}

type ProTab = 'photos' | 'custom-frames' | 'themes' | 'collections' | 'icons' | 'stats';

const AdminMPhotoPro: React.FC<AdminMPhotoProProps> = ({ userId }) => {
  const { pages: t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('proTab');
  const activeTab: ProTab =
    tabParam === 'custom-frames' ||
    tabParam === 'themes' ||
    tabParam === 'collections' ||
    tabParam === 'icons' ||
    tabParam === 'stats'
      ? tabParam
      : 'photos';

  const [mountedTabs, setMountedTabs] = useState<Set<ProTab>>(() => new Set([activeTab]));

  useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  const setActiveTab = (tab: ProTab) => {
    if (tab === 'photos') {
      searchParams.delete('proTab');
      setSearchParams(searchParams, { replace: true });
    } else {
      searchParams.set('proTab', tab);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const tabOptions = useMemo(
    () => [
      { value: 'photos' as const, label: t.mPhotoPro.tabs.photos, icon: <PhotoLibrary fontSize="inherit" /> },
      { value: 'custom-frames' as const, label: t.mPhotoPro.tabs.customFrames, shortLabel: t.mPhotoPro.tabs.customFramesShort, icon: <Crop fontSize="inherit" /> },
      { value: 'themes' as const, label: t.mPhotoPro.tabs.themes, shortLabel: t.mPhotoPro.tabs.themesShort, icon: <Palette fontSize="inherit" /> },
      { value: 'collections' as const, label: t.mPhotoPro.tabs.collections, icon: <FolderSpecial fontSize="inherit" /> },
      { value: 'icons' as const, label: t.mPhotoPro.tabs.icons, icon: <Apps fontSize="inherit" /> },
      { value: 'stats' as const, label: t.mPhotoPro.tabs.stats, icon: <BarChartIcon fontSize="inherit" /> },
    ],
    [t],
  );

  return (
    <Box>
      <MPhotoSectionTabs
        value={activeTab}
        onChange={setActiveTab}
        options={tabOptions}
        accentColor="#0f3460"
        sticky
      />

      {mountedTabs.has('photos') && (
        <Box sx={{ display: activeTab === 'photos' ? 'block' : 'none' }}>
          <AdminPhotos userId={userId} />
        </Box>
      )}
      {mountedTabs.has('custom-frames') && (
        <Box sx={{ display: activeTab === 'custom-frames' ? 'block' : 'none' }}>
          <CustomFrames userId={userId} channel="pro" variant="user" embedded />
        </Box>
      )}
      {mountedTabs.has('themes') && (
        <Box sx={{ display: activeTab === 'themes' ? 'block' : 'none' }}>
          <ThemeList userId={userId} channel="pro" embedded />
        </Box>
      )}
      {mountedTabs.has('collections') && (
        <Box sx={{ display: activeTab === 'collections' ? 'block' : 'none' }}>
          <UserCollections userId={userId} />
        </Box>
      )}
      {mountedTabs.has('icons') && (
        <Box sx={{ display: activeTab === 'icons' ? 'block' : 'none' }}>
          <AdminIcons userId={userId} channel="pro" />
        </Box>
      )}
      {mountedTabs.has('stats') && (
        <Box sx={{ display: activeTab === 'stats' ? 'block' : 'none' }}>
          <GalleryStatsPanel channel="pro" userId={userId} accentColor="#0f3460" />
        </Box>
      )}
    </Box>
  );
};

export default AdminMPhotoPro;
